import useQueryAction from "../../../lib/useQueryAction";
import useQueryMutation from "../../../lib/useQueryMutation";
import { InvitationsFilters } from "../../../pages/dashboard/submenu/Invitations";
import {
  api,
  makeGetRequest,
  makePostRequest,
  makePatchRequest,
} from "../../api";

export const useUsers = (userId: string, token?: string) => {
  return useQueryAction({
    queryFn: () => makeGetRequest(api.users.getUser(userId)),
    queryKey: [userId],
  });
};
export const useInvitation = (params: InvitationsFilters) => {
  // Only enable the query if we have valid parameters
  const isEnabled = params !== undefined;

  const queryKey = [
    "invitations",
    params?.page,
    params?.pageSize,
    params?.email,
  ];

  return useQueryAction({
    queryFn: () => makeGetRequest(`${api.invitations.invitation}`, params),
    queryKey,
    cacheTime: 5 * 60 * 1000,
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};
export const useGetUser = (userId: string) => {
  return useQueryAction({
    queryFn: () => makeGetRequest(api.users.getUser(userId)),
    queryKey: ["id"],
  });
};
export const useBusinessUserDetails = (businessId?: string) => {
  return useQueryAction({
    queryFn: () => makeGetRequest(api.users.usersBusinessDetails(businessId)),
    queryKey: [businessId],
  });
};

export const useBusinessKycStatus = () => {
  return useQueryMutation({
    mutationFn: (data) =>
      makePatchRequest(data, api.admin.businessKycStatus),
  });
};

export const useInvitees = (userId: string) => {
  return useQueryAction({
    queryFn: () => makeGetRequest(api.users.invitees(userId)),
    queryKey: ["invitee"],
  });
};
export const useRejectionReasons = () => {
  return useQueryAction({
    queryFn: () => makeGetRequest(api.users.rejectionReasons),
    queryKey: ["rejection"],
  });
};

export const useRejection = () => {
  return useQueryMutation({
    mutationFn: (data: any) => makePostRequest(data, api.users.rejection),
  });
};

export const useRequestOtp = (userId: string) => {
  return useQueryMutation({
    mutationFn: () => makeGetRequest(api.users.otpRequest(userId)),
  });
};

export const useVerifyUserMutation = () => {
  return useQueryMutation({
    mutationFn: (data: { email: string; password: string }) =>
      makePostRequest(data, api.users.verify),
  });
};
export const useBusinessDocDecision = () => {
  return useQueryMutation({
    mutationFn: (data: {
      documentId: string;
      status: string;
      rejectionReason: string;
    }) => makePostRequest(data, api.users.businessDocumentDecision),
  });
};
export const useBusinessKYCDecision = () => {
  return useQueryMutation({
    mutationFn: (data: { businessId: string; kycStatus: string }) =>
      makePostRequest(data, api.users.businessDocumentDecision),
  });
};
export const useBusinessContactDecision = () => {
  return useQueryMutation({
    mutationFn: (data: { businessContactId: string; kycStatus: string }) =>
      makePostRequest(data, api.users.businessDocumentDecision),
  });
};

export const useSignUpVerifyMutation = () => {
  return useQueryMutation({
    mutationFn: (data: { verificationCode: string }) =>
      makePostRequest(data, api.users.signUpVerify),
  });
};

export const useUpdateBank = () => {
  return useQueryMutation({
    mutationFn: (data: { accountNumber: string; bankName: string }) =>
      makePatchRequest(data, api.users.updateBank),
  });
};

export const useRequestAccess = () => {
  return useQueryMutation({
    mutationFn: (data: {
      email: string;
      firstName: string;
      lastName: string;
      referralCode: string;
    }) => makePostRequest(data, api.users.requestAccess),
  });
};

export const useInvitationVerify = () => {
  return useQueryMutation({
    mutationFn: (data: { token: string; code: string }) =>
      makePostRequest(data, api.users.invitationVerify),
  });
};

export const useIndividualKyc = () => {
  return useQueryMutation({
    mutationFn: (data: {
      bvn: string;
      dob: string;
      firstname: string;
      lastname: string;
    }) => makePostRequest(data, api.users.individualKyc),
  });
};

export const useUser = () => {
  return useQueryMutation({
    mutationFn: (data: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      phone: string;
      refereeCode: string;
      nuban: string;
      bank: string;
      accountType: string;
    }) => makePostRequest(data, api.users.users),
  });
};

export const useBusinessKyc = () => {
  return useQueryMutation({
    mutationFn: (data: { cacRegNo: string; businessType: string }) =>
      makePostRequest(data, api.users.businessKyc),
  });
};

// export const useUserLogin = () => {
//   return useQueryMutation({
//     mutationFn: (data: { verificationCode: string; email: string }) =>
//       makePostRequest(data, api.users.userLogin),
//   });
// };

export const useUserLogin = () => {
  return useQueryMutation({
    mutationFn: async (data: {
      loginToken: string;
      email: string;
      verificationCode: string;
      deviceIdentifier?: string;
      devicePassCode?: string;
    }) => {
      try {
        return await makePostRequest(data, api.users.userLogin);
      } catch (error) {
        // handleError(error, "Failed to process user login");
        // throw error;
      }
    },
  });
};

export const useCheckEmail = () => {
  return useQueryMutation({
    mutationFn: (data: { email: string }) =>
      makePostRequest(data, api.users.checkEmail),
  });
};

export const useRecoverPassword = () => {
  return useQueryMutation({
    mutationFn: (data: { email: string }) =>
      makePostRequest(data, api.users.recoverPassword),
  });
};

export const useResetPassword = () => {
  return useQueryMutation({
    mutationFn: (data: {
      email: string;
      newPassword: string;
      verificationCode: string;
}) =>
      makePostRequest(data, api.users.resetPassword),
  });
};
export const useUsersInvitation = () => {
  return useQueryMutation({
    mutationFn: (data: {
      email: string;
      lastName: string;
      firstName: string;
    }) => makePostRequest(data, api.users.usersInvitations),
  });
};

export const useGenerateBeneficiaryForm = () => {
  return useQueryMutation({
    mutationFn: () =>
      makePostRequest(undefined, api.users.generateBeneficiaryForm),
  });
};

 export const useUserRefreshUserToken =  () => {
     return useQueryMutation({
    mutationFn: async (data: { refreshToken: string, accessToken: string }) => {
      try {
        return await makePostRequest(data, api.users.refreshUserToken);
      } catch (error) {
        // handleError(error, "Failed to refresh user token");
        // throw error;
      }
    },
  });
    };
